import { useState } from "react";

import { torontoDateParts } from "@utils";

import { ADP_PAY_PERIODS } from "../../utils/contants";

// Convert "MM/DD/YYYY" (as stored in ADP_PAY_PERIODS) to the "YYYY-MM-DD"
// format required by a native <input type="date" />.
const toInputDate = (usDate) => {
  if (!usDate) return "";
  const [month, day, year] = usDate.split("/");
  if (!month || !day || !year) return "";
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

// Convert a pay-period label like "June 2026 (1)" to the "YYYY-MM" form the
// netsuite search-by-month-year API expects (e.g. "2026-06").
export const periodToMonthYear = (label) => {
  if (!label) return "";
  const [name, year] = label.split(/\s+/);
  const idx = MONTH_NAMES.indexOf((name || "").toLowerCase());
  if (idx === -1 || !year) return "";
  return `${year}-${String(idx + 1).padStart(2, "0")}`;
};

// Pick the first pay period whose label matches the current calendar month.
export const getDefaultPayPeriod = () => {
  const { year, month } = torontoDateParts();
  // Build a Date for Toronto's current month purely to format its long name.
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long" });
  const prefix = `${monthName} ${year}`;

  const match = ADP_PAY_PERIODS.find((p) => p.month.startsWith(prefix));
  return match || ADP_PAY_PERIODS[0];
};

// Full value emitted to consumers for a given pay-period record.
export const buildPayPeriodValue = (p) => ({
  period: p.month,
  month: periodToMonthYear(p.month),
  startDate: toInputDate(p.start),
  endDate: toInputDate(p.end),
});

// Default value (incl. "YYYY-MM" month) for seeding parent state.
export const getDefaultPayPeriodValue = () => buildPayPeriodValue(getDefaultPayPeriod());

// Pass `value` ({ period, startDate, endDate }) to make this a controlled component whose
// selection is driven (and can be persisted, e.g. to the URL) by the parent — used by the
// admin ADP Netsuite "View" tab. Omit `value` for the uncontrolled behavior (own local state,
// reset to the current period on mount) — used by the Upload tab's independent period picker.
export default function PayPeriod({ onChange, allowEmpty = false, value }) {
  const defaultPeriod = allowEmpty ? null : getDefaultPayPeriod();
  const isControlled = value != null;

  const [localPeriod, setLocalPeriod] = useState(allowEmpty ? "" : defaultPeriod.month);
  const [localStart, setLocalStart] = useState(allowEmpty ? "" : toInputDate(defaultPeriod.start));
  const [localEnd, setLocalEnd] = useState(allowEmpty ? "" : toInputDate(defaultPeriod.end));

  const period = isControlled ? value.period || "" : localPeriod;
  const startDate = isControlled ? value.startDate || "" : localStart;
  const endDate = isControlled ? value.endDate || "" : localEnd;

  const emit = (next) => {
    onChange?.({ period, month: periodToMonthYear(period), startDate, endDate, ...next });
  };

  const handlePeriodChange = (e) => {
    const month = e.target.value;
    const selected = ADP_PAY_PERIODS.find((p) => p.month === month);

    const nextStart = selected ? toInputDate(selected.start) : "";
    const nextEnd = selected ? toInputDate(selected.end) : "";

    if (!isControlled) {
      setLocalPeriod(month);
      setLocalStart(nextStart);
      setLocalEnd(nextEnd);
    }
    emit({
      period: month,
      month: periodToMonthYear(month),
      startDate: nextStart,
      endDate: nextEnd,
    });
  };

  const handleStartChange = (e) => {
    if (!isControlled) setLocalStart(e.target.value);
    emit({ startDate: e.target.value });
  };

  const handleEndChange = (e) => {
    if (!isControlled) setLocalEnd(e.target.value);
    emit({ endDate: e.target.value });
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
      <div className="w-full sm:w-[200px]">
        <label className="block text-xs font-semibold text-palette-slate-badge mb-1">Period</label>
        <select
          className="w-full rounded-lg border border-palette-gray-divider bg-white px-3 py-2 text-sm"
          value={period}
          onChange={handlePeriodChange}
        >
          {allowEmpty && (
            <option value="" disabled>
              — Select a period —
            </option>
          )}
          {ADP_PAY_PERIODS.map((p) => (
            <option key={p.month} value={p.month}>
              {p.month}
            </option>
          ))}
        </select>
      </div>

      <div className="w-full sm:w-[170px]">
        <label className="block text-xs font-semibold text-palette-slate-badge mb-1">
          Start Date
        </label>
        <input
          type="date"
          className="w-full rounded-lg border border-palette-gray-divider bg-white px-3 py-2 text-sm"
          value={startDate}
          onChange={handleStartChange}
        />
      </div>

      <div className="w-full sm:w-[170px]">
        <label className="block text-xs font-semibold text-palette-slate-badge mb-1">
          End Date
        </label>
        <input
          type="date"
          className="w-full rounded-lg border border-palette-gray-divider bg-white px-3 py-2 text-sm"
          value={endDate}
          onChange={handleEndChange}
        />
      </div>
    </div>
  );
}
